import React from "react";

const TermsOfServiceModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const styles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 9999,
      padding: "1rem",
      overflowY: "auto",
    },
    modal: {
      backgroundColor: "white",
      borderRadius: "12px",
      maxWidth: "900px",
      width: "100%",
      maxHeight: "90vh",
      display: "flex",
      flexDirection: "column",
      boxShadow:
        "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    },
    header: {
      padding: "1.5rem 2rem",
      borderBottom: "2px solid #e5e7eb",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      position: "sticky",
      top: 0,
      backgroundColor: "white",
      borderTopLeftRadius: "12px",
      borderTopRightRadius: "12px",
    },
    title: {
      fontSize: "1.875rem",
      fontWeight: "700",
      color: "#1f2937",
      margin: 0,
    },
    closeButton: {
      background: "none",
      border: "none",
      fontSize: "1.5rem",
      cursor: "pointer",
      color: "#6b7280",
      padding: "0.5rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "6px",
      transition: "all 0.2s",
    },
    content: {
      padding: "2rem",
      overflowY: "auto",
      fontFamily: "system-ui, -apple-system, sans-serif",
      lineHeight: "1.6",
      color: "#333",
    },
    subtitle: {
      fontSize: "0.95rem",
      color: "#6b7280",
      fontWeight: "500",
      marginBottom: "2rem",
    },
    section: {
      marginBottom: "2rem",
    },
    sectionTitle: {
      fontSize: "1.25rem",
      fontWeight: "600",
      color: "#374151",
      marginBottom: "0.75rem",
      paddingBottom: "0.5rem",
      borderBottom: "1px solid #e5e7eb",
    },
    subsectionTitle: {
      fontSize: "1.1rem",
      fontWeight: "600",
      color: "#4b5563",
      marginBottom: "0.5rem",
      marginTop: "1rem",
    },
    paragraph: {
      marginBottom: "0.75rem",
      color: "#374151",
      fontSize: "0.95rem",
    },
    list: {
      paddingLeft: "1.5rem",
      marginBottom: "0.75rem",
    },
    listItem: {
      marginBottom: "0.4rem",
      color: "#374151",
      fontSize: "0.95rem",
    },
    strong: {
      fontWeight: "600",
      color: "#1f2937",
    },
    contact: {
      backgroundColor: "#f9fafb",
      padding: "1.25rem",
      borderRadius: "8px",
      border: "1px solid #e5e7eb",
      marginTop: "1rem",
    },
    important: {
      backgroundColor: "#fef3cd",
      border: "1px solid #fbbf24",
      borderRadius: "6px",
      padding: "1rem",
      marginBottom: "1rem",
    },
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h1 style={styles.title}>Terms of Service</h1>
          <button
            style={styles.closeButton}
            onClick={onClose}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#f3f4f6";
              e.target.style.color = "#1f2937";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "transparent";
              e.target.style.color = "#6b7280";
            }}
          >
            ✕
          </button>
        </div>

        <div style={styles.content}>
          <p style={styles.subtitle}>
            <strong>Effective Date:</strong> January 1, 2024 |{" "}
            <strong>Last Updated:</strong> January 1, 2024
          </p>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>1. Agreement to Terms</h2>
            <p style={styles.paragraph}>
              By accessing or using Arkila's transportation booking platform and
              administrative systems ("Services"), you agree to be bound by
              these Terms of Service ("Terms"). If you do not agree to these
              Terms, please do not use our Services.
            </p>
            <p style={styles.paragraph}>These Terms apply to:</p>
            <ul style={styles.list}>
              <li style={styles.listItem}>All users of the Arkila platform</li>
              <li style={styles.listItem}>
                Transportation operators and drivers
              </li>
              <li style={styles.listItem}>Administrative personnel</li>
              <li style={styles.listItem}>Third-party integrators</li>
            </ul>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>2. Description of Services</h2>

            <h3 style={styles.subsectionTitle}>2.1 Platform Overview</h3>
            <p style={styles.paragraph}>
              Arkila provides a digital platform that connects passengers with
              jeepney transportation services, featuring:
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                <span style={styles.strong}>Booking System:</span> Real-time
                reservation and scheduling
              </li>
              <li style={styles.listItem}>
                <span style={styles.strong}>Payment Processing:</span> Secure
                digital payment options
              </li>
              <li style={styles.listItem}>
                <span style={styles.strong}>Location Services:</span> GPS
                tracking and navigation
              </li>
              <li style={styles.listItem}>
                <span style={styles.strong}>Administrative Tools:</span>{" "}
                Management dashboard for operators and administrators
              </li>
            </ul>

            <h3 style={styles.subsectionTitle}>2.2 Service Availability</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                Services are available in designated coverage areas
              </li>
              <li style={styles.listItem}>
                Operating hours may vary by location and operator
              </li>
              <li style={styles.listItem}>
                Service availability subject to vehicle capacity and demand
              </li>
            </ul>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              3. User Accounts and Registration
            </h2>

            <h3 style={styles.subsectionTitle}>3.1 Account Creation</h3>
            <p style={styles.paragraph}>To use our Services, you must:</p>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                Provide accurate and complete information
              </li>
              <li style={styles.listItem}>
                Maintain the security of your account credentials
              </li>
              <li style={styles.listItem}>Update information as necessary</li>
              <li style={styles.listItem}>
                Be at least 18 years of age or have parental consent
              </li>
            </ul>

            <h3 style={styles.subsectionTitle}>3.2 Account Responsibilities</h3>
            <p style={styles.paragraph}>You are responsible for:</p>
            <ul style={styles.list}>
              <li style={styles.listItem}>All activities under your account</li>
              <li style={styles.listItem}>
                Maintaining confidentiality of login credentials
              </li>
              <li style={styles.listItem}>
                Promptly notifying us of unauthorized access
              </li>
              <li style={styles.listItem}>
                Ensuring accuracy of provided information
              </li>
            </ul>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              4. User Conduct and Prohibited Activities
            </h2>

            <h3 style={styles.subsectionTitle}>4.1 Acceptable Use</h3>
            <p style={styles.paragraph}>Users must:</p>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                Use Services for lawful purposes only
              </li>
              <li style={styles.listItem}>
                Treat other users and operators with respect
              </li>
              <li style={styles.listItem}>
                Provide accurate booking information
              </li>
              <li style={styles.listItem}>
                Follow safety guidelines and instructions
              </li>
            </ul>

            <h3 style={styles.subsectionTitle}>4.2 Prohibited Activities</h3>
            <div style={styles.important}>
              <p style={styles.paragraph}>
                <strong>You may not:</strong>
              </p>
            </div>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                <span style={styles.strong}>Misuse the Platform:</span> Attempt
                to circumvent security measures
              </li>
              <li style={styles.listItem}>
                <span style={styles.strong}>Fraudulent Behavior:</span> Provide
                false information or engage in payment fraud
              </li>
              <li style={styles.listItem}>
                <span style={styles.strong}>Harassment:</span> Abuse, threaten,
                or harass other users
              </li>
              <li style={styles.listItem}>
                <span style={styles.strong}>Illegal Activities:</span> Use
                Services for any unlawful purpose
              </li>
              <li style={styles.listItem}>
                <span style={styles.strong}>Unauthorized Access:</span> Access
                accounts or data not belonging to you
              </li>
            </ul>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>5. Booking and Payment Terms</h2>

            <h3 style={styles.subsectionTitle}>5.1 Booking Process</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                Bookings are subject to availability
              </li>
              <li style={styles.listItem}>
                Confirmation required for all reservations
              </li>
              <li style={styles.listItem}>
                Cancellation policies apply as specified
              </li>
              <li style={styles.listItem}>
                Special requirements must be disclosed during booking
              </li>
            </ul>

            <h3 style={styles.subsectionTitle}>5.2 Payment Terms</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                Payment required at time of booking or service completion
              </li>
              <li style={styles.listItem}>
                All prices include applicable taxes
              </li>
              <li style={styles.listItem}>
                Refunds processed according to our refund policy
              </li>
              <li style={styles.listItem}>
                Processing time for refunds: 5-10 business days
              </li>
            </ul>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>6. Transportation Services</h2>

            <h3 style={styles.subsectionTitle}>6.1 Service Standards</h3>
            <p style={styles.paragraph}>Transportation operators must:</p>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                Maintain valid licenses and permits
              </li>
              <li style={styles.listItem}>
                Follow safety protocols and regulations
              </li>
              <li style={styles.listItem}>Provide clean and safe vehicles</li>
              <li style={styles.listItem}>Treat passengers respectfully</li>
            </ul>

            <h3 style={styles.subsectionTitle}>6.2 Service Limitations</h3>
            <div style={styles.important}>
              <p style={styles.paragraph}>
                <strong>Important:</strong>
              </p>
              <ul style={styles.list}>
                <li style={styles.listItem}>
                  We do not own or operate vehicles
                </li>
                <li style={styles.listItem}>
                  Transportation provided by independent operators
                </li>
                <li style={styles.listItem}>
                  Service quality may vary between operators
                </li>
              </ul>
            </div>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>7. Privacy and Data Protection</h2>
            <p style={styles.paragraph}>
              Our Privacy Policy governs the collection and use of your
              information. By using our Services, you consent to our privacy
              practices as outlined in our Privacy Policy.
            </p>

            <h3 style={styles.subsectionTitle}>7.1 Location Data</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                GPS location required for certain services
              </li>
              <li style={styles.listItem}>
                Location data used for navigation and safety
              </li>
              <li style={styles.listItem}>
                Users can manage location preferences in settings
              </li>
            </ul>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>8. Limitation of Liability</h2>

            <h3 style={styles.subsectionTitle}>8.1 Service Disclaimer</h3>
            <p style={styles.paragraph}>
              Services are provided "as is" without warranties of any kind. We
              disclaim all warranties, express or implied, including
              merchantability, fitness for a particular purpose, and
              non-infringement.
            </p>

            <h3 style={styles.subsectionTitle}>8.2 Liability Limits</h3>
            <p style={styles.paragraph}>
              To the fullest extent permitted by law, our liability is limited
              to:
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>Direct damages only</li>
              <li style={styles.listItem}>
                Maximum amount equal to fees paid in the last 12 months
              </li>
              <li style={styles.listItem}>
                No consequential, indirect, or punitive damages
              </li>
            </ul>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>9. Regulatory Compliance</h2>

            <h3 style={styles.subsectionTitle}>
              9.1 Transportation Regulations
            </h3>
            <p style={styles.paragraph}>
              Our platform operates in compliance with:
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                Land Transportation and Franchising Regulatory Board (LTFRB)
                regulations
              </li>
              <li style={styles.listItem}>Local transportation ordinances</li>
              <li style={styles.listItem}>
                Safety and environmental standards
              </li>
              <li style={styles.listItem}>Insurance requirements</li>
            </ul>

            <h3 style={styles.subsectionTitle}>9.2 Data Protection</h3>
            <p style={styles.paragraph}>
              We comply with applicable data protection laws including:
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                Data Privacy Act of 2012 (Philippines)
              </li>
              <li style={styles.listItem}>
                International data protection standards where applicable
              </li>
            </ul>
          </div>

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>10. Contact Information</h2>
            <div style={styles.contact}>
              <p style={styles.paragraph}>
                <span style={styles.strong}>Legal Department</span>
              </p>
              <ul style={styles.list}>
                <li style={styles.listItem}>Email: legal@arkila.com</li>
                <li style={styles.listItem}>Phone: +63 (2) 8XXX-XXXX</li>
              </ul>

              <p style={styles.paragraph}>
                <span style={styles.strong}>Customer Support</span>
              </p>
              <ul style={styles.list}>
                <li style={styles.listItem}>Email: support@arkila.com</li>
                <li style={styles.listItem}>Phone: +63 (2) 8XXX-XXXX</li>
                <li style={styles.listItem}>
                  Hours: Monday-Friday, 8:00 AM - 6:00 PM PST
                </li>
              </ul>
            </div>
          </div>

          <div style={styles.contact}>
            <p style={styles.paragraph}>
              <em>
                These Terms of Service were last updated on January 1, 2024. For
                the most current version, please visit our website or contact
                our legal department.
              </em>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServiceModal;
